import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {GeographyFilterOptions, AppliedGeographyFilter, IGeographyFilterPayload, FetchComplianceResponse} from '../types';
import {isAbortError} from '../../../../lib/error-status';
import {parseFilterValues, joinFilterValues} from '../../components/no-filters/utils/parse-filter-values';
import {MAX_SPECIALTY_SELECTION} from '../../components/hooks/use-specialty-filter';

/**
 * Manages geography filters (state, county, standard type, patient roster, zip) and specialty filters.
 * Loads available filter options and specialty data for network adequacy analysis.
 */
export class GeographyStore {
    availableGeographyFilters: GeographyFilterOptions[] = [];
    appliedGeographyFilter: AppliedGeographyFilter = {
        state: '',
        county: '',
        standard_type: '',
        patient_roster_name: '',
        standard_version: '',
    };
    specialtyFilter = '';
    maxSpecialtySelection = MAX_SPECIALTY_SELECTION;
    specialties: string[] = [];
    complianceScores: FetchComplianceResponse = [];
    defaultComplianceScores: FetchComplianceResponse = [];
    loadingGeographyData = false;
    loadingSpecialtyData = false;
    private currentSpecialtyController: AbortController | null = null;

    constructor() {
        // getGeographyFiltersPayload and getUniqueValuesByKey are read-only queries.
        // Marking them false prevents makeAutoObservable from annotating them as actions,
        // which would make their observable reads untracked and cause the geographyFilters
        // computed on the main store to cache stale data permanently.
        makeAutoObservable(this, {
            getGeographyFiltersPayload: false,
            getUniqueValuesByKey: false,
        } as any);
    }

    async loadGeographyData(runId: number, controller: AbortController): Promise<void> {
        runInAction(() => {
            this.loadingGeographyData = true;
        });

        try {
            const response = await networkOptimization.fetchGeographyFilters(runId, controller.signal);
            runInAction(() => {
                this.availableGeographyFilters = response || [];
                this.loadingGeographyData = false;
            });
        } catch (error) {
            // Don't reset loading state on abort - a new request is already in progress
            if (error instanceof Error && isAbortError(error)) {
                throw error;
            }

            runInAction(() => {
                this.loadingGeographyData = false;
            });
            throw error;
        }
    }

    async loadSpecialtiesData(
        runId: number,
        revisionId: number,
        distanceCalcType: string,
        filters: IGeographyFilterPayload,
        appliedState: string,
        appliedCounty: string,
        controller: AbortController,
        provider_facility_type = '',
        includeStateAndCounty = true
    ): Promise<void> {
        runInAction(() => {
            this.currentSpecialtyController = controller;
            this.loadingSpecialtyData = true;
        });

        try {
            let requestFilters = {
                ...filters,
                provider_facility_type
            };

            if (provider_facility_type || !includeStateAndCounty) {
                requestFilters = {...requestFilters, state: '', county: ''};
            }

            const adequateUnitLables: { [key: string]: string } = {
                npi: '# of Distinct Providers',
                npi_lat_long: '# of Provider Locations',
                beds: '# of Beds'
            };

            const isStateAndCountySelected = appliedState && appliedCounty && includeStateAndCounty;
            const result = await networkOptimization.fetchSpecialtyReport(
                requestFilters,
                runId,
                revisionId,
                distanceCalcType,
                controller.signal
            );
            const specialtyReport = result.adequacy_data;

            runInAction(() => {
                if (isStateAndCountySelected) {
                    this.specialties = Array.from(new Set(specialtyReport.map((item: any) => item.specialty_code)));
                    this.complianceScores = specialtyReport.map((item: any) => ({
                        name: item.specialty_name,
                        specialtyCode: item.specialty_code,
                        compliance: item.final_adequacy_flag === '1',
                        revision_id: item.revision_id === '1',
                        compliance_target: (item.compliance_target * 100).toFixed(1),
                        scores: [
                            {name: 'Time', value: this.formatPercentage(item.population_met_time), compliant: item.time_adequacy_flag === '1'},
                            {name: 'Distance', value: this.formatPercentage(item.population_met_distance), compliant: item.distance_adequacy_flag === '1'},
                            {name: adequateUnitLables[item.minimum_required_method] || '# of Provider Locations', value: item.total_adequate_units, compliant: item.min_docs_flag === '1'},
                            {name: 'Estimated Total Compliance', value: null, compliant: item.final_adequacy_flag === '1', cssClasses: item.final_adequacy_flag === '1' ? 'bg-green-300 font-bold' : 'bg-red-300 font-bold'},
                            {name: 'Min Required', value: item.minimum_required, compliant: item.min_required_adequacy_flag < item.total_adequate_units}
                        ]
                    }));
                    // Clear defaultComplianceScores after new data is set to ensure correct data source is used
                    this.defaultComplianceScores = [];
                } else {
                    this.complianceScores = specialtyReport;
                    this.defaultComplianceScores = result.default_specialty_filter_data.map((item: any) => ({
                        name: item.specialty_name,
                        specialtyCode: item.specialty_code
                    }));
                }
            });
        } finally {
            runInAction(() => {
                // Only reset loading if this request is still the current one
                if (this.currentSpecialtyController === controller) {
                    this.loadingSpecialtyData = false;
                }
            });
        }
    }

    /**
     * Set geography filter with cascading defaults
     * When setting higher-level filters (standard_type, standard_version), resets dependent filters
     * @param resetSpecialty - Whether to reset specialty filter (default: true)
     */
    setGeographyFilter(filterName: keyof AppliedGeographyFilter, value: string, resetSpecialty = true): void {
        if (this.appliedGeographyFilter[filterName] !== value) {
            this.appliedGeographyFilter = {...this.appliedGeographyFilter, [filterName]: value};
            switch (filterName) {
            case 'standard_type': {
                const defaultStandardVersion = this.getUniqueValuesByKey('standard_version', {'standard_type': this.appliedGeographyFilter.standard_type}, '', 'DESC')[0];
                const defaultPatientRosterName = this.getUniqueValuesByKey('patient_roster_name', {'standard_type': this.appliedGeographyFilter.standard_type, 'standard_version': defaultStandardVersion})[0] || '';
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, standard_version: defaultStandardVersion, patient_roster_name: defaultPatientRosterName, state: '', county: ''};
                break;
            }
            case 'standard_version': {
                const defaultPatientRoster = this.getUniqueValuesByKey('patient_roster_name', {'standard_type': this.appliedGeographyFilter.standard_type, 'standard_version': this.appliedGeographyFilter.standard_version})[0] || '';
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, patient_roster_name: defaultPatientRoster, state: '', county: ''};
                break;
            }
            case 'patient_roster_name': {
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, state: '', county: ''};
                break;
            }
            case 'state': {
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, county: ''};
                break;
            }}
            if (resetSpecialty) {
                this.specialtyFilter = '';
            }
        }
    }

    resetGeographyFilter(preserve: Array<keyof AppliedGeographyFilter> = []): void {
        const current = this.appliedGeographyFilter;
        const standardType = preserve.includes('standard_type')
            ? current.standard_type
            : (this.getUniqueValuesByKey('standard_type')[0] || '');

        const standardVersion = preserve.includes('standard_version')
            ? current.standard_version
            : (this.getUniqueValuesByKey('standard_version', {standard_type: standardType})[0] || '');

        const patientRosterName = preserve.includes('patient_roster_name')
            ? current.patient_roster_name
            : (this.getUniqueValuesByKey('patient_roster_name', {standard_type: standardType, standard_version: standardVersion})[0] || '');

        const state = preserve.includes('state') ? current.state : '';
        const county = preserve.includes('county') ? current.county : '';

        this.appliedGeographyFilter = {
            state,
            county,
            patient_roster_name: patientRosterName,
            standard_type: standardType,
            standard_version: standardVersion
        };
        this.specialtyFilter = '';
    }

    setSpecialtyFilter(value: string): void {
        const newCodes = parseFilterValues(value);

        if (newCodes.length > this.maxSpecialtySelection) {
            return;
        }

        this.specialtyFilter = value;
    }

    toggleSpecialtyFilter(specialtyCode: string): void {
        const codes = parseFilterValues(this.specialtyFilter);
        const index = codes.indexOf(specialtyCode);

        if (index > -1) {
            codes.splice(index, 1);
        } else {
            if (codes.length < this.maxSpecialtySelection) {
                codes.push(specialtyCode);
            }
        }

        this.specialtyFilter = joinFilterValues(codes);
    }

    clearSpecialtyFilters(): void {
        this.specialtyFilter = '';
    }

    isSpecialtySelected(specialtyCode: string): boolean {
        if (!this.specialtyFilter) return false;
        const codes = parseFilterValues(this.specialtyFilter);
        return codes.includes(specialtyCode);
    }

    canSelectMoreSpecialties(): boolean {
        const codes = parseFilterValues(this.specialtyFilter);
        return codes.length < this.maxSpecialtySelection;
    }

    getSelectedSpecialtyCount(): number {
        return parseFilterValues(this.specialtyFilter).length;
    }

    /**
     * Get geography filters as payload for API calls
     * Processes pipe-separated values and resolves filter IDs
     */
    getGeographyFiltersPayload(): IGeographyFilterPayload {
        // Helper function to process pipe-separated values
        const processPipeSeparatedValue = (
            value: string,
            filterArray: GeographyFilterOptions[],
            findFn: (item: GeographyFilterOptions, val: string) => boolean,
            extractFn: (item: GeographyFilterOptions) => string | undefined
        ): string => {
            if (!value) return '';

            if (value.includes('|')) {
                const values = parseFilterValues(value);
                const results = values
                    .map(v => {
                        const found = filterArray.find(item => findFn(item, v));
                        return found ? extractFn(found) : null;
                    })
                    .filter(r => r !== undefined && r !== null && r !== '');
                return joinFilterValues(results as string[]);
            }

            const found = filterArray.find(item => findFn(item, value));
            return found ? (extractFn(found) || '') : '';
        };

        const state = processPipeSeparatedValue(
            this.appliedGeographyFilter.state,
            this.availableGeographyFilters,
            (item, val) => item?.state_additional_info?.state === val,
            (item) => item?.state
        );

        const county = processPipeSeparatedValue(
            this.appliedGeographyFilter.county,
            this.availableGeographyFilters,
            (item, val) => `${item?.county_additional_info?.county_name}-${item.ssacd}` === val,
            (item) => item?.ssacd
        );

        const patient_roster_id = this.availableGeographyFilters.find(
            o => o?.patient_roster_name === this.appliedGeographyFilter.patient_roster_name
        )?.patient_roster_id;

        const standard_version = this.appliedGeographyFilter.standard_version;
        const standard_type = this.appliedGeographyFilter.standard_type;

        return {
            state: state || '',
            county: county || '',
            standard_version: standard_version || '',
            standard_type: standard_type || '',
            patient_roster_id: patient_roster_id || ''
        };
    }

    /**
     * Get unique values for a filter key with optional filtering and sorting
     */
    getUniqueValuesByKey(
        key: string | string[],
        filter?: Record<string, string>,
        separator?: string,
        sortOrder?: 'ASC' | 'DESC'
    ): string[] {
        let selectedFilters = this.availableGeographyFilters;

        if (separator === undefined || separator === null) {
            separator = '-';
        }

        if (sortOrder === undefined || sortOrder === null) {
            sortOrder = 'DESC';
        }

        // Helper to get nested value by path (array of keys)
        const getValueByPath = (obj: any, path: string | string[]) => {
            const keys = Array.isArray(path) ? path : path.split('.');
            return keys.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
        };

        // Apply filter if provided
        if (filter && Object.keys(filter).length > 0) {
            selectedFilters = selectedFilters.filter(item =>
                Object.entries(filter).every(([fKey, fVal]) => {
                    // Skip empty filter values
                    if (!fVal || fVal === '') return true;

                    const val = getValueByPath(item, fKey);

                    // Handle pipe-separated values (multiple selections)
                    if (fVal.includes('|')) {
                        const filterValues = parseFilterValues(fVal);
                        return filterValues.includes(val);
                    }

                    return val === fVal;
                })
            );
        }

        const values = selectedFilters
            .map(item => {
                if (Array.isArray(key)) {
                    const parts = key.map(k => getValueByPath(item, k));
                    return parts.every(p => typeof p === 'string') ? parts.join(separator) : undefined;
                } else {
                    return getValueByPath(item, key);
                }
            })
            .filter(val => typeof val === 'string');

        // Unique values
        const uniqueValues = Array.from(new Set(values));

        // Sort values
        return sortOrder === 'ASC'
            ? uniqueValues.sort()
            : uniqueValues.sort().reverse();
    }

    private formatPercentage(value: number): string {
        return `${(value * 100).toFixed(1)}%`;
    }

    /**
     * Reset geography store state to initial values
     */
    reset(): void {
        runInAction(() => {
            this.availableGeographyFilters = [];
            this.appliedGeographyFilter = {
                state: '',
                county: '',
                standard_type: '',
                patient_roster_name: '',
                standard_version: '',
            };
            this.defaultComplianceScores = [];
            this.complianceScores = [];
        });
    }
}
